import {
  Box,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateExamMenu from "../../../components/student/CreateExamMenu";
import StudentPortalShell from "../../../components/student/StudentPortalShell";
import TagExamPaperModal from "../../../components/student/TagExamPaperModal";
import ExamMainData from "../../../containers/student/exams/exam-main-data";
import { studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { getStudentExamsRequest } from "../../../modules/students-modules/student-exams/Actions";
import {
  studentSolveExamPath,
  studentExamResultPath,
} from "../../../utils/studentExamPaths";
import { loadTagExamSession } from "../../../utils/studentTagExam";

const C = {
  primary: studentPalette.primary,
  muted: studentSurfaces.textMuted,
};

const StudentExamsPage = () => {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const studentExams = useSelector((state) => state.studentExams.studentExams);
  const isLoading = useSelector((state) => state.studentExams.isLoading);

  useEffect(() => {
    dispatch(getStudentExamsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!returnTo) {
      try {
        sessionStorage.removeItem("edutopia_post_exam_redirect");
      } catch {
        /* ignore */
      }
    }
  }, [returnTo]);

  const handleSolveExam = (data) => {
    navigate(studentSolveExamPath(data?.id, data?.name));
  };

  const handleCheckExamAnswers = (data) => {
    navigate(studentExamResultPath(data?.id, data?.name));
  };

  const session = loadTagExamSession();
  const sessionExamId = session?.exam?.id;
  const sessionQuestions = Array.isArray(session?.questions) ? session.questions : [];
  const [paperOpen, setPaperOpen] = useState(false);

  const examList = (Array.isArray(studentExams) ? studentExams : []).map((item) => {
    if (item?.exam && typeof item.exam === "object" && item.id == null) {
      return { ...item.exam, tags: item.tags || item.exam.tags };
    }
    return item;
  });

  return (
    <StudentPortalShell>
      <Box p={{ base: 4, md: 8 }} maxW="container.2xl" w="100%" minW={0}>
        <VStack align="stretch" spacing={4} mb={8} w="100%">
          <Box minW={0}>
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="800"
              color={C.primary}
              fontFamily="'Tajawal','Amiri',sans-serif"
              mb={2}
              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
            >
              Test yourself
            </Heading>
            <Text fontSize="sm" color={C.muted}>
              Build a practice exam from tags and track your progress.
            </Text>
          </Box>
          <CreateExamMenu />
        </VStack>

        {isLoading && examList.length === 0 ? (
          <Flex justify="center" py={16}>
            <Spinner size="lg" color={C.primary} />
          </Flex>
        ) : examList.length === 0 ? (
          <Text color={C.muted} fontWeight="600">
            No practice exams yet. Select tags and a question count to create one.
          </Text>
        ) : (
          <>
            <Text
              fontSize="sm"
              fontWeight="800"
              color={C.primary}
              textTransform="uppercase"
              letterSpacing="0.12em"
              mb={4}
              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
            >
              Your practice exams
            </Text>
            <Grid
              templateColumns="repeat(auto-fill, minmax(min(100%, 280px), 1fr))"
              gap={6}
              w="100%"
              minW={0}
            >
              {examList.map((exam) => (
                <ExamMainData
                  key={exam.id}
                  exam={exam}
                  state={exam?.right_answers == null ? "Pending" : "Solved"}
                  solveExamFun={handleSolveExam}
                  checkExamAnswersFun={handleCheckExamAnswers}
                  returnTo={returnTo}
                  allowStart={false}
                  showStatus={false}
                  alwaysShowCheckAnswers
                  icon="tag"
                  onViewPdf={
                    sessionQuestions.length && String(sessionExamId) === String(exam.id)
                      ? () => setPaperOpen(true)
                      : undefined
                  }
                  canContinue={
                    exam?.right_answers == null &&
                    sessionExamId != null &&
                    String(sessionExamId) === String(exam.id)
                  }
                />
              ))}
            </Grid>
          </>
        )}
      </Box>
      <TagExamPaperModal
        isOpen={paperOpen}
        onClose={() => setPaperOpen(false)}
        title={session?.exam?.name || "Practice exam"}
        questions={sessionQuestions}
        backLabel="Back"
      />
    </StudentPortalShell>
  );
};

export default StudentExamsPage;
